import { inject } from "vue";
import type { App, InjectionKey, Plugin } from "vue";
import type api from "@/utils/api";
import type Cookie from "@/utils/cookie";
import type Constants from "@/utils/constants";
import type { Router } from "vue-router";

// Injection keys
export const apiKey: InjectionKey<typeof api> = Symbol.for("blade.api");
export const cookieKey: InjectionKey<typeof Cookie> = Symbol.for("blade.cookie");
export const constantsKey: InjectionKey<typeof Constants> = Symbol.for("blade.constants");
export const routerKey: InjectionKey<Router> = Symbol.for("blade.router");

// Helper with error handling
const getOrThrow = <T>(key: InjectionKey<T>, name: string): T => {
  const value = inject(key);
  if (!value) throw new Error(`${name} is not provided`);
  return value;
};

// Composables
export const useApi = () => getOrThrow(apiKey, "api");
export const useConstants = () => getOrThrow(constantsKey, "constants");
export const useCookie = () => getOrThrow(cookieKey, "cookie");
export const useRouterInstance = () => getOrThrow(routerKey, "router");

// Plugin
export const createAppGlobalsPlugin = (
  apiObj: typeof api,
  cookieObj: typeof Cookie,
  constantsObj: typeof Constants,
  routerObj: Router,
): Plugin => ({
  install: (app: App) => {
    app.provide(apiKey, apiObj);
    app.provide(cookieKey, cookieObj);
    app.provide(constantsKey, constantsObj);
    app.provide(routerKey, routerObj);
  },
});
